import { request } from '@/api/client'
import type { Paginated } from '@/api/types'

export interface DashboardResponse {
  consultations_today: number
  followups_today: number
  followups_overdue: number
  patients_total: number
  patients_new_week: number
  documents_total: number
  consultations_per_day: { day: string; consultations: number }[]
}

export interface FollowUp {
  patient_id: string
  op_number: string
  full_name: string
  age: number | null
  sex: string
  due_on: string
  last_seen_on: string
  days_overdue: number
}

export interface PrescriptionRow {
  /** Which consultation wrote it. Everything sharing this is one prescription,
   *  not several medicines that happen to fall on the same date. */
  visit_id: string
  position: number
  patient_id: string
  op_number: string
  full_name: string
  age: number | null
  sex: string
  drug_name: string
  dose: string | null
  frequency: string | null
  duration: string | null
  instructions: string | null
  visit_date: string
}

export type FollowUpWindow = 'overdue' | 'today' | 'week' | 'all'

export const insights = {
  dashboard: (signal?: AbortSignal) =>
    request<DashboardResponse>('/v1/dashboard', signal ? { signal } : {}),
  /** `total` is the size of the window, which the row limit may not reach. */
  followups: (window: FollowUpWindow, limit: number, signal?: AbortSignal) =>
    request<{ data: FollowUp[]; total: number }>('/v1/followups', {
      query: { window, limit },
      ...(signal ? { signal } : {}),
    }),
  /** `limit` counts consultations, not medicines — the server never splits one
   *  prescription across two pages, so a page's row count varies. */
  prescriptions: (q: string, limit: number, cursor: string | null, signal?: AbortSignal) =>
    request<Paginated<PrescriptionRow>>('/v1/prescriptions', {
      query: { q: q || undefined, limit, cursor: cursor || undefined },
      ...(signal ? { signal } : {}),
    }),
}
